import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    GetTentConversations,
    GetTentMessages,
    applyTentSocketEvent,
} from '../redux/tentSlice';
import { openTentChatSocket } from '../utils/tentChatSocket';
import {
    playTentReceiveSound,
    unlockTentChatSounds,
} from '../utils/tentChatSounds';
import store from '../redux/store';

// #region agent log
const agentLog = (location, message, data, hypothesisId) => {
    fetch('http://127.0.0.1:7798/ingest/539ad776-defd-4048-9a51-bd9937645f01', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '9d55d5' },
        body: JSON.stringify({
            sessionId: '9d55d5',
            runId: 'pre-fix',
            hypothesisId,
            location,
            message,
            data,
            timestamp: Date.now(),
        }),
    }).catch(() => {});
};

const tentSnapshot = () => {
    const tent = store.getState()?.tent;
    const map = tent?.messagesByConversation;
    let buckets = 0;
    let messageCount = 0;
    let badBuckets = 0;
    if (map && typeof map === 'object') {
        Object.values(map).forEach(value => {
            buckets += 1;
            if (Array.isArray(value)) messageCount += value.length;
            else badBuckets += 1;
        });
    }
    const memory = performance?.memory
        ? {
              used: performance.memory.usedJSHeapSize,
              total: performance.memory.totalJSHeapSize,
              limit: performance.memory.jsHeapSizeLimit,
          }
        : null;
    return {
        conversations: Array.isArray(tent?.conversations) ? tent.conversations.length : null,
        buckets,
        messageCount,
        badBuckets,
        memory,
    };
};
// #endregion

/**
 * Keep a Tent chat WebSocket open for the whole Tent page (People + Chat)
 * so incoming messages update the thread and unread badge in realtime.
 */
export default function useTentChatRealtime() {
    const dispatch = useDispatch();
    const selectedConversationId = useSelector(state => state.tent.selectedConversationId);
    const currentUserId = useSelector(state => state.user?.user?.id);
    const conversations = useSelector(state => state.tent.conversations);
    const { selectedProject } = useSelector(state => state.projectData);

    const socketRef = useRef(null);
    const selectedIdRef = useRef(selectedConversationId);
    const currentUserIdRef = useRef(currentUserId);
    const conversationIdsRef = useRef(new Set());
    const reconnectTimerRef = useRef(null);
    const cancelledRef = useRef(false);
    const connectingRef = useRef(false);

    useEffect(() => {
        selectedIdRef.current = selectedConversationId;
    }, [selectedConversationId]);

    useEffect(() => {
        currentUserIdRef.current = currentUserId;
    }, [currentUserId]);

    useEffect(() => {
        conversationIdsRef.current = new Set((conversations || []).map(item => item.id));
    }, [conversations]);

    useEffect(() => {
        const unlock = () => unlockTentChatSounds();
        window.addEventListener('pointerdown', unlock, { once: true });
        window.addEventListener('keydown', unlock, { once: true });
        return () => {
            window.removeEventListener('pointerdown', unlock);
            window.removeEventListener('keydown', unlock);
        };
    }, []);

    useEffect(() => {
        const projectId = selectedProject?.id || localStorage.getItem('project_id');
        if (!projectId) return undefined;

        cancelledRef.current = false;
        let attempt = 0;
        let authFailed = false;

        const isAuthError = error => {
            const status = error?.response?.status;
            return status === 401 || status === 403;
        };

        const clearReconnect = () => {
            if (reconnectTimerRef.current) {
                window.clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        };

        const scheduleReconnect = () => {
            if (cancelledRef.current || authFailed) return;
            attempt += 1;
            // Cap at 60s so a dead Daphne does not hammer ws-ticket every few seconds.
            const delay = Math.min(60000, 2000 * 2 ** Math.min(attempt - 1, 5));
            clearReconnect();
            // #region agent log
            agentLog(
                'useTentChatRealtime.js:scheduleReconnect',
                'reconnect scheduled',
                { attempt, delay, visibility: document.visibilityState, connecting: connectingRef.current },
                'H2',
            );
            // #endregion
            reconnectTimerRef.current = window.setTimeout(() => {
                connect();
            }, delay);
        };

        const catchUp = () => {
            dispatch(GetTentConversations());
            const conversationId = selectedIdRef.current;
            if (conversationId) {
                dispatch(GetTentMessages({ conversationId }));
            }
        };

        const connect = async () => {
            // #region agent log
            agentLog(
                'useTentChatRealtime.js:connect',
                'connect called',
                {
                    cancelled: cancelledRef.current,
                    connecting: connectingRef.current,
                    hasSocket: Boolean(socketRef.current),
                    readyState: socketRef.current?.socket?.readyState ?? null,
                    attempt,
                    visibility: document.visibilityState,
                    ...tentSnapshot(),
                },
                'H1',
            );
            // #endregion
            if (cancelledRef.current || authFailed || connectingRef.current) return;
            if (socketRef.current?.socket?.readyState === WebSocket.OPEN) return;
            connectingRef.current = true;
            try {
                const connection = await openTentChatSocket(projectId, {
                    onEvent: payload => {
                        if (payload?.event === 'message.created') {
                            const message = payload.payload;
                            const conversationId = message?.conversation_id;
                            const senderId = String(message?.sender?.user_id || '');
                            const me = String(currentUserIdRef.current || '');
                            const fromSelf = Boolean(senderId && me && senderId === me);
                            dispatch(applyTentSocketEvent({ ...payload, fromSelf }));
                            if (conversationId && !conversationIdsRef.current.has(conversationId)) {
                                dispatch(GetTentConversations());
                            }
                            if (!message || message.message_type === 'system' || fromSelf) return;
                            playTentReceiveSound();
                            return;
                        }
                        dispatch(applyTentSocketEvent(payload));
                    },
                    onOpen: () => {
                        attempt = 0;
                        connectingRef.current = false;
                    },
                    onClose: () => {
                        const wasLive = socketRef.current != null;
                        socketRef.current = null;
                        connectingRef.current = false;
                        // #region agent log
                        agentLog(
                            'useTentChatRealtime.js:onClose',
                            'hook close',
                            { wasLive, cancelled: cancelledRef.current, visibility: document.visibilityState },
                            'H1',
                        );
                        // #endregion
                        if (cancelledRef.current) return;
                        if (wasLive) scheduleReconnect();
                    },
                });
                if (cancelledRef.current) {
                    connection.close();
                    connectingRef.current = false;
                    return;
                }
                socketRef.current = connection;
                // If the socket already failed during ticket→open, clear and retry with backoff.
                if (
                    connection.socket.readyState === WebSocket.CLOSED ||
                    connection.socket.readyState === WebSocket.CLOSING
                ) {
                    socketRef.current = null;
                    connectingRef.current = false;
                    scheduleReconnect();
                }
            } catch (error) {
                socketRef.current = null;
                connectingRef.current = false;
                if (isAuthError(error)) {
                    authFailed = true;
                    clearReconnect();
                }
                // #region agent log
                agentLog(
                    'useTentChatRealtime.js:connectFail',
                    'connect failed',
                    {
                        cancelled: cancelledRef.current,
                        message: String(error?.message || 'connect failed').slice(0, 240),
                        status: error?.response?.status || null,
                        visibility: document.visibilityState,
                    },
                    'H2',
                );
                // #endregion
                if (cancelledRef.current) return;
                scheduleReconnect();
            }
        };

        connect();

        let hiddenAt = document.visibilityState === 'hidden' ? Date.now() : 0;
        const onVisible = () => {
            const hiddenMs = hiddenAt ? Date.now() - hiddenAt : 0;
            if (document.visibilityState === 'hidden') hiddenAt = Date.now();
            // #region agent log
            agentLog(
                'useTentChatRealtime.js:visibility',
                'visibility change',
                {
                    visibility: document.visibilityState,
                    hiddenMs,
                    connecting: connectingRef.current,
                    hasSocket: Boolean(socketRef.current),
                    readyState: socketRef.current?.socket?.readyState ?? null,
                    ...tentSnapshot(),
                },
                'H1',
            );
            // #endregion
            if (document.visibilityState !== 'visible' || authFailed) return;
            catchUp();
            if (!socketRef.current) connect();
        };
        document.addEventListener('visibilitychange', onVisible);

        const onPageEvent = event => {
            // #region agent log
            agentLog(
                'useTentChatRealtime.js:page',
                'page lifecycle',
                {
                    type: event.type,
                    persisted: Boolean(event.persisted),
                    connecting: connectingRef.current,
                    readyState: socketRef.current?.socket?.readyState ?? null,
                    ...tentSnapshot(),
                },
                'H5',
            );
            // #endregion
        };
        window.addEventListener('pageshow', onPageEvent);
        window.addEventListener('pagehide', onPageEvent);
        document.addEventListener('freeze', onPageEvent);
        document.addEventListener('resume', onPageEvent);

        const onWindowError = event => {
            // #region agent log
            agentLog(
                'useTentChatRealtime.js:windowError',
                'window error',
                {
                    message: String(event?.message || '').slice(0, 300),
                    filename: event?.filename || '',
                    lineno: event?.lineno || null,
                    stack: String(event?.error?.stack || '').slice(0, 500),
                },
                'H4',
            );
            // #endregion
        };
        const onRejection = event => {
            const reason = event?.reason;
            // #region agent log
            agentLog(
                'useTentChatRealtime.js:rejection',
                'unhandled rejection',
                {
                    message: String(reason?.message || reason || '').slice(0, 300),
                    stack: String(reason?.stack || '').slice(0, 500),
                },
                'H4',
            );
            // #endregion
        };
        window.addEventListener('error', onWindowError);
        window.addEventListener('unhandledrejection', onRejection);

        const originalConsoleError = console.error;
        console.error = (...args) => {
            const text = args
                .map(item => (item && item.stack ? `${item.message}\n${item.stack}` : String(item)))
                .join('\n')
                .slice(0, 800);
            if (
                /The above error occurred|Uncaught|Maximum update depth|Minified React error|Cannot read propert|is not a function|QuotaExceeded/i.test(
                    text,
                )
            ) {
                // #region agent log
                agentLog('useTentChatRealtime.js:consoleError', 'console error', { text }, 'H4');
                // #endregion
            }
            originalConsoleError.apply(console, args);
        };

        return () => {
            cancelledRef.current = true;
            clearReconnect();
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('pageshow', onPageEvent);
            window.removeEventListener('pagehide', onPageEvent);
            document.removeEventListener('freeze', onPageEvent);
            document.removeEventListener('resume', onPageEvent);
            window.removeEventListener('error', onWindowError);
            window.removeEventListener('unhandledrejection', onRejection);
            console.error = originalConsoleError;
            socketRef.current?.close();
            socketRef.current = null;
            connectingRef.current = false;
        };
    }, [dispatch, selectedProject?.id]);
}
